import { TFunction } from "i18next";

type TWebsiteBuilderMigrationErrorData =
  | { type: "undefined-schema-version" }
  | { type: "unsupported-future-schema-version"; schemaVersion: number; currentSchemaVersion: number }
  | { type: "no-migration-exists"; schemaVersion: number }
  | { type: "cannot-migrate-from-schema-version"; schemaVersion: number; nextSchemaVersion: number }
  | { type: "element-missing-field"; schemaVersion: number; nextSchemaVersion: number }
  | { type: "container-missing-field"; schemaVersion: number; nextSchemaVersion: number }
  | { type: "section-missing-field"; schemaVersion: number; nextSchemaVersion: number };

export class WebsiteBuilderMigrationError extends Error {
  readonly data: TWebsiteBuilderMigrationErrorData;

  constructor(data: TWebsiteBuilderMigrationErrorData) {
    super(data.type);
    this.name = WebsiteBuilderMigrationError.name;
    this.data = data;

    Object.setPrototypeOf(this, WebsiteBuilderMigrationError.prototype);
  }

  public getMessage(t: TFunction) {
    let message = t("Could not migrate website content.");
    
    switch (this.data.type) {
      case "undefined-schema-version": {
        message = t("Schema version is missing.");
        break;
      }
      case "unsupported-future-schema-version": {
        message = t("Schema version {{schemaVersion}} is newer than the latest supported version ({{currentSchemaVersion}}).", {
          schemaVersion: this.data.schemaVersion,
          currentSchemaVersion: this.data.currentSchemaVersion,
        });
        break;
      }
      case "no-migration-exists": {
        message = t(
          "No migration exists for schema version {{schemaVersion}}.", {
            schemaVersion: this.data.schemaVersion,
          },
        );
        break;
      }
      case "cannot-migrate-from-schema-version": {
        message = t(
          "Cannot migrate from schema version {{schemaVersion}} to {{nextSchemaVersion}}.", {
            schemaVersion: this.data.schemaVersion,
            nextSchemaVersion: this.data.nextSchemaVersion,
          },
        );
        break;
      }
      case "element-missing-field": {
        message = t(
          "Element is missing required data to migrate from schema version {{schemaVersion}} to {{nextSchemaVersion}}.", {
            schemaVersion: this.data.schemaVersion,
            nextSchemaVersion: this.data.nextSchemaVersion,
          },
        );
        break;
      }
      case "container-missing-field": {
        message = t(
          "Container is missing required data to migrate from schema version {{schemaVersion}} to {{nextSchemaVersion}}.", {
            schemaVersion: this.data.schemaVersion,
            nextSchemaVersion: this.data.nextSchemaVersion,
          });
        break;
      }
      case "section-missing-field": {
        message = t(
          "Section is missing required data to migrate from schema version {{schemaVersion}} to {{nextSchemaVersion}}.", {
            schemaVersion: this.data.schemaVersion,
            nextSchemaVersion: this.data.nextSchemaVersion,
          },
        );
        break;
      }
    }

    return message;
  }
}
